import { ScrollToService, ScrollToConfigOptions } from '@nicky-lenaers/ngx-scroll-to';
import { FlowOrigin, StorageService } from './../shared/storage.service';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  ChangeDetectorRef,
  OnDestroy
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ControlMessagesComponent,
  Static,
  Tarea,
  TareaCondicionAdmin,
  TareaFinalizacion,
  TareaOpcionAdmin,
  TareaCondicion,
  TypeProcess
} from '@app/shared';
import { environment } from '@env/environment';
import { isEmpty, result } from 'lodash';
import { DragulaService } from 'ng2-dragula';
import { ManageCompletionComponent } from '../manage-completion/manage-completion.component';
import { ManageTaskService } from '../shared/manage-task.service';
import { RefactoringCompletionsComponent } from '../refactoring-completions/refactoring-completions.component';
import { Subject } from 'rxjs';

type TypeFormArray = 'tarea_condicion' | 'opciones';

@Component({
  selector: 'iw-manage-conditions',
  templateUrl: './manage-conditions.component.html',
  styleUrls: ['./manage-conditions.component.scss'],
})
export class ManageConditionsComponent implements OnInit, OnDestroy {
  // Contiene el tipo de proceso que se esta renderizando
  @Input() typeProcess: TypeProcess;
  @Input() sourceTaskI: Tarea;
  sourceTask: Tarea;
  @Input() flowIsSynchronized = false;
  // Origen del flujo
  @Input() origin: FlowOrigin;
  // Referencia del local storage
  @Input() ss: StorageService;
  // Obtiene las asociaciones de la tarea
  @Input()
  set listAssociations (list: any[]) {
    this._listAssociations = list;
  }
  // Formulario que controla las condiciones con sus opciones
  form: FormGroup;
  // Para mostrar información de debug.
  debug: boolean = environment.showDebugHelpers;
  // Define el efecto collapse en las finalizaciones
  isCollapsed: Array<Array<boolean>> = [];
  // Mensajes de validación personalizados
  validationMessagesPersonalizaction = {
    pattern: 'Caracteres invalidos: solo letras y guiones bajos para los alias de las asociaciones.'
  };
  // Destruye y gestiona el memory leak
  private destroy: Subject<void> = new Subject<void>();
  // Emite los cambios realizados en el formulario
  @Output() saved = new EventEmitter<TareaCondicionAdmin>();
  // Componenetes encargados de controlar los mensajes de error de los controles
  @ViewChildren(ControlMessagesComponent) messageComponents: QueryList<ControlMessagesComponent>;
  // Componentes encargados de manejar las finalizaciones de las opciones
  @ViewChildren(RefactoringCompletionsComponent) manageCompletions: QueryList<RefactoringCompletionsComponent>;
  // Contiene las asociaciones de la tarea actual
  private _listAssociations: any[] = [];

  constructor(
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private ds: DragulaService,
    public mts: ManageTaskService,
    private sts: ScrollToService,
  ) {
    // Configuraciones para la bolsa de condiciones (dragula)
    if (!this.ds.find('bag-conditions')) {
      ds.setOptions('bag-conditions', {
        moves: (el: any, container: any, handle: any) => {
          const hasClass = Static.nativeElementHasClass(handle, 'drag-condition');
          return hasClass;
        }
      });
    }
    // Configuraciones para la bolsa de opciones (dragula)
    if (!this.ds.find('bag-options')) {
      ds.setOptions('bag-options', {
        moves: (el: any, container: any, handle: any) => {
          const hasClass = Static.nativeElementHasClass(handle, 'drag-option');
          return hasClass;
        }
      });
    }
  }

  ngOnInit() {
    this.sourceTask = Static.copyObjet(this.sourceTaskI);
    if (this.sourceTask && this.sourceTask.tarea_condicion && this.sourceTask.tarea_condicion.length) {
      for (let index = 0; index < this.sourceTask.tarea_condicion.length; index++) {
        const collapseOptions: Array<boolean> = [];
        if (
          this.sourceTask.tarea_condicion[index]['opciones'] &&
          this.sourceTask.tarea_condicion[index]['opciones'].length
        ) {
          this.sourceTask.tarea_condicion[index]['opciones'].forEach(() => {
            collapseOptions.push(false);
          });
        } else {
          collapseOptions.push(false);
        }
        this.isCollapsed.push(collapseOptions);
      }
    } else {
      this.isCollapsed.push([false]);
    }
    this.reset(result(this.sourceTask, 'tarea_condicion'));
  }

  ngOnDestroy() {
    this.destroy.next();
    this.destroy.complete();
  }

  get listAssociations (): any[] {
    return this._listAssociations;
  }

  /**
   * Inicializa el formulario para una nueva condición.
   *
   * @private
   * @returns
   * @memberof ManageConditionsComponent
   */
  private initCondition(data: TareaCondicionAdmin = null) {
    if (data) {
      const options: FormGroup[] = [];
      data.opciones.forEach(opt => {
        options.push(this.initOption(opt));
      });
      return this.fb.group({
        condicion: [data.condicion, [Validators.required]],
        alias: [data.alias, [Validators.required, Validators.pattern('^[a-zA-Z_]*$')]],
        opciones: this.fb.array(options)
      });
    }
    return this.fb.group({
      condicion: ['', [Validators.required]],
      alias: ['', [Validators.required, Validators.pattern('^[a-zA-Z_]*$')]],
      opciones: this.fb.array([this.initOption()])
    });
  }

  /**
   * Inicializa el formulario para una nueva opción.
   *
   * @private
   * @returns
   * @memberof ManageConditionsComponent
   */
  private initOption(data: TareaOpcionAdmin = null) {
    if (data) {
      return this.fb.group({
        opcion: [data.opcion, Validators.required],
        mascara: [data.mascara],
        orden: [data.orden],
        id_opcion: [data.id_opcion]
      });
    }
    return this.fb.group({
      opcion: ['', Validators.required],
      mascara: [''],
      orden: [1],
      id_opcion: [this.ss.applyNextId('option')]
    });
  }

  /**
   * Consulta los controles asociados a las condiciones o opciones.
   *
   * @param {*} group
   * @param {TypeFormArray} key
   * @returns {AbstractControl[]}
   * @memberof ManageConditionsComponent
   */
  get(group: any, key: TypeFormArray): AbstractControl[] {
    return group.controls[key].controls;
  }

  /**
   * Agrega un nuevo control a las condiciones u opciones.
   *
   * @param {*} group
   * @param {TypeFormArray} key
   * @param {(TareaCondicionAdmin | TareaOpcionAdmin)} [data=null]
   * @param {number} [indexCondiction=-1]
   * @memberof ManageConditionsComponent
   */
  add(
    group: any,
    key: TypeFormArray,
    data: TareaCondicionAdmin | TareaOpcionAdmin = null,
    indexCondiction: number = -1) {
    const initFn =
      key === 'tarea_condicion'
        ? this.initCondition(data as TareaCondicionAdmin)
        : this.initOption(data as TareaOpcionAdmin);
    const control = <FormArray>group.get(key);
    if (key === 'opciones') {
      if (control && control.length && !data) {
        initFn.controls['orden'].setValue(control.length + 1);
        initFn.controls['id_opcion'].setValue(this.ss.applyNextId('option'));
      }
    }
    control.push(initFn);
    this.isCollapsed.push([false]);
    if (indexCondiction !== -1) {
      setTimeout(() => {
        this.sts.scrollTo({
          target: 'condiction_' + indexCondiction + 'opction_' + initFn.controls['orden'].value
        } as ScrollToConfigOptions);
      });
    } else {
      if (key === 'tarea_condicion' && !data) {
        setTimeout(() => {
          this.sts.scrollTo({
            target: 'condiction_' + (control.length - 1)
          } as ScrollToConfigOptions);
        });
      }
    }
  }

  /**
   * Elimina un control de las condiciones u opciones.
   *
   * @param {*} group
   * @param {TypeFormArray} key
   * @param {number} i
   * @param {number} [indexCondiction=-1]
   * @memberof ManageConditionsComponent
   */
  remove(group: any, key: TypeFormArray, i: number, indexCondiction: number = -1) {
    let opt = -1;
    const control = <FormArray>group.get(key);
    if (key === 'opciones') {
      for (let index = 0; index < control.controls.length; index++) {
        if (index > i) {
          control.controls[index]['controls']['orden'].setValue(control.controls[index]['controls']['orden'].value - 1);
        } else if (index === i) {
          opt = control.controls[index]['controls']['orden'].value - 1;
        }
      }
      this.isCollapsed[indexCondiction].splice(i, 1);
    } else {
      this.isCollapsed[i].splice(i, 1);
    }
    control.removeAt(i);
    if (opt !== -1 && indexCondiction !== -1) {
      setTimeout(() => {
        this.sts.scrollTo({
          target: 'condiction_' + indexCondiction + 'opction_' + opt
        } as ScrollToConfigOptions);
      });
    } else if (key === 'tarea_condicion') {
      if (i === 0) {
        opt = i;
      } else {
        opt = i - 1;
      }
      setTimeout(() => {
        this.sts.scrollTo({
          target: 'condiction_' + opt
        } as ScrollToConfigOptions);
      });
    }
  }

  /**
   * Bajar opcion en 1
   *
   * @param {*} group
   * @param {number} i
   * @param {number} [indexCondiction=-1]
   * @memberof ManageConditionsComponent
   */
  goDownOption(group: any, i: number, indexCondiction: number = -1) {
    let opt = -1;
    const control = <FormArray>group.get('opciones');
    for (let index = 0; index < control.controls.length; index++) {
      if (index === i) {
        control.controls[index]['controls']['orden'].setValue(control.controls[index]['controls']['orden'].value + 1);
        opt = control.controls[index]['controls']['orden'].value;
      } else if (index === (i + 1)) {
        control.controls[index]['controls']['orden'].setValue(control.controls[index]['controls']['orden'].value - 1);
      }
    }
    control.controls.sort((a, b) => a['controls']['orden'].value - b['controls']['orden'].value);
    this.cdRef.detectChanges();
    if (opt !== -1 && indexCondiction !== -1) {
      setTimeout(() => {
        this.sts.scrollTo({
          target: 'condiction_' + indexCondiction + 'opction_' + opt
        } as ScrollToConfigOptions);
      });
    }
  }

  /**
   * Subir opcion en 1
   *
   * @param {*} group
   * @param {number} i
   * @param {number} [indexCondiction=-1]
   * @memberof ManageConditionsComponent
   */
  goUpOption(group: any, i: number, indexCondiction: number = -1) {
    let opt = -1;
    const control = <FormArray>group.get('opciones');
    for (let index = 0; index < control.controls.length; index++) {
      if (index === i) {
        control.controls[index]['controls']['orden'].setValue(control.controls[index]['controls']['orden'].value - 1);
        opt = control.controls[index]['controls']['orden'].value;
      } else if (index === (i - 1)) {
        control.controls[index]['controls']['orden'].setValue(control.controls[index]['controls']['orden'].value + 1);
      }
    }
    control.controls.sort((a, b) => a['controls']['orden'].value - b['controls']['orden'].value);
    this.cdRef.detectChanges();
    if (opt !== -1 && indexCondiction !== -1) {
      setTimeout(() => {
        this.sts.scrollTo({
          target: 'condiction_' + indexCondiction + 'opction_' + opt
        } as ScrollToConfigOptions);
      });
    }
  }

  /**
   * Emite los cambios realizados en el formulario.
   *
   * @memberof ManageConditionsComponent
   */
  save() {
    this.saved.emit(this.form.value);
  }

  /**
   * Retorna los datos del formulario.
   *
   * @returns
   * @memberof ManageConditionsComponent
   */
  getData(): TareaCondicionAdmin[] {
    const values = this.form.value.tarea_condicion;
    // Si alguna key es vacia
    const someKeyIsEmpty = values.some((el: any) => {
      if (isEmpty(el.condicion)) {
        return true;
      } else {
        return el.opciones.some((el2: any) => {
          if (isEmpty(el2.opcion)) {
            return true;
          }
        });
      }
    });
    return someKeyIsEmpty ? null : values;
  }

  /**
   * Retorna un objeto con las finalizaciones de todas
   * las opciones de todas las condiciones.
   *
   * @memberof ManageConditionsComponent
   */
  getCompletions() {
    const completions: { [key: number]: TareaFinalizacion[] } = {};
    this.manageCompletions.map(el => {
      if (!completions.hasOwnProperty(el.indexCondition)) {
        completions[el.indexCondition] = [];
      }
      const completiosOption = el.getCompletion();
      if (completiosOption && completiosOption.length) {
        completiosOption.forEach(compl => {
          completions[el.indexCondition].push(compl);
        });
      }
    });
    return completions;
  }

  /**
   * Determina si la información del componente es valida
   * para crear las condiciones.
   *
   * @returns {boolean}
   * @memberof ManageConditionsComponent
   */
  isValidData(): boolean {
    let isValid = true;
    this.manageCompletions.map(el => {
      if (isValid) {
        isValid = el.isValidData();
      }
    });
    this.validateAllControls();
    isValid = this.form.valid && isValid;
    return isValid;
  }

  /**
   * Validar todos los controles del formulario.
   *
   * @memberof ManageConditionsComponent
   */
  validateAllControls() {
    this.messageComponents.forEach(el => {
      el.validateControl(true);
    });
  }

  /**
   * Resetea el componente.
   *
   * @param {TareaCondicionAdmin[]} [data]
   * @memberof ManageConditionsComponent
   */
  reset(data?: TareaCondicionAdmin[] | TareaCondicion[]) {
    // Inicializar formulario vacio
    this.form = this.fb.group({
      tarea_condicion: this.fb.array([])
    });
    if (data && data.length) {
      if (this.flowIsSynchronized) {
        data = Static.getMapConditions(<TareaCondicion[]>data);
      }
      // Inicializar formulario con valores por defecto
      (<TareaCondicionAdmin[]>data).forEach((condition) => {
        this.add(this.form, 'tarea_condicion', condition);
      });
    } else {
      // Agregar el primer item del array de condiciones
      this.add(this.form, 'tarea_condicion');
    }
    this.cdRef.detectChanges();
  }
} // End class



// WEBPACK FOOTER //
// ./src/app/administration/process-automation/manage-conditions/manage-conditions.component.ts